// frontend/src/api/platformApi.js
// Platform API calls (auth, currency, beta keys) extracted from DatabaseAPI.js
// Phase 1 Platform Extraction — Step 15B

import { API_BASE, ENDPOINTS, getCsrfHeaders } from './transport';
import { handleApiAuthError } from '../auth/authUtils';

// PR1 Platform Identity Foundation — friendship/search/leaderboard endpoints now
// key on UserProfile.public_id (UUID4 string), not the raw integer User.id.
// Simple shape check only — the backend is the source of truth for validity.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function validateBetaKey(betaKey) {
    // The key is never logged. Shared `GRUP_` keys are multi-use and revocable,
    // and console output leaks into screen shares, bug-report screenshots, and
    // session-replay capture. Log that validation happened, not the secret.
    console.log('DatabaseAPI: Validating beta key');

    // AllowAny endpoint hit pre-login, but still send CSRF + credentials so the
    // request is identical in shape to every other mutating helper (and so it
    // keeps working if the view is ever tightened to IsAuthenticated).
    return fetch(`${API_BASE}/${ENDPOINTS.AUTH.VALIDATE_BETA_KEY}/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
            beta_key: betaKey
        })
    })
    .then(response => {
        console.log(`DatabaseAPI: Beta key validation response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Beta key validation failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to validate beta key`);
            });
        }

        return response.json();
    })
    .then(data => {
        // `data` embeds the full key record (including `key_code`) — log only
        // the outcome, for the same reason as above.
        console.log(`DatabaseAPI: Beta key validation complete, valid=${data.valid}`);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error validating beta key:', error);
        throw error;
    });
}

export function registerUser(userData) {
    console.log(`DatabaseAPI: Registering user: ${userData.email}`);

    return fetch(`${API_BASE}/${ENDPOINTS.AUTH.REGISTER}/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(userData)
    })
    .then(response => {
        console.log(`DatabaseAPI: User registration response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: User registration failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to register user`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: User registration successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error registering user:', error);
        throw error;
    });
}

export function loginUser(credentials) {
    // PR2 Slice F4/F5: never log the submitted email or any credential field.
    console.log('DatabaseAPI: Logging in user');

    return fetch(`${API_BASE}/${ENDPOINTS.AUTH.LOGIN}/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(credentials)
    })
    .then(response => {
        console.log(`DatabaseAPI: User login response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: User login failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to login user`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`🔍 DATABASEAPI: Raw login response from backend:`, data);
        console.log(`🔍 DATABASEAPI: Response user object:`, data.user);
        console.log(`🔍 DATABASEAPI: User ID from backend:`, data.user?.id, `(type: ${typeof data.user?.id})`);

        // CRITICAL VALIDATION: Ensure we're getting Django User.id (integer), not UUID
        if (data.user && typeof data.user.id !== 'number') {
            console.error(`❌ DATABASEAPI: BACKEND STILL RETURNING NON-INTEGER USER ID!`);
            console.error(`❌ DATABASEAPI: Expected integer Django User.id, got:`, data.user.id, typeof data.user.id);
            throw new Error(`Backend authentication fix failed - still getting UUID instead of Django User.id`);
        }

        console.log(`✅ DATABASEAPI: Login response validation passed - Django User.id: ${data.user.id}`);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error logging in user:', error);
        throw error;
    });
}

export function logoutUser() {
    console.log(`DatabaseAPI: Logging out user`);

    // Logout is a session-authenticated POST — it MUST carry CSRF, or DRF's
    // SessionAuthentication rejects it and the user can never sign out.
    return fetch(`${API_BASE}/${ENDPOINTS.AUTH.LOGOUT}/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: User logout response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: User logout failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to logout user`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: User logout successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error logging out user:', error);
        throw error;
    });
}

export function getCurrentUser() {
    return fetch(`${API_BASE}/${ENDPOINTS.AUTH.CURRENT_USER}/`, {
        credentials: 'include'
    })
        .then(response => {
            console.log(`DatabaseAPI: Get current user response status: ${response.status}`);

            if (!response.ok) {
                // CurrentUserView uses DRF IsAuthenticated with SessionAuthentication,
                // which returns 403 (not 401) for an anonymous session. For this
                // identity probe, both statuses mean "no authenticated user" and
                // must not trigger the global hard redirect.
                if (response.status === 401 || response.status === 403) {
                    return null;
                }

                const error = { status: response.status };
                if (handleApiAuthError(error)) {
                    throw new Error('Authentication failed - redirected to login');
                }

                throw new Error(`HTTP ${response.status}: Failed to get current user`);
            }

            return response.json();
        })
        .then(data => {
            console.log('🔍 DATABASEAPI: Raw getCurrentUser response from backend:', data);
            console.log('🔍 DATABASEAPI: Current user object:', data?.user);
            console.log('🔍 DATABASEAPI: Current user ID from backend:', data?.user?.id, `(type: ${typeof data?.user?.id})`);

            // CRITICAL VALIDATION: Ensure we're getting Django User.id (integer), not UUID
            if (data?.user && typeof data.user.id !== 'number') {
                console.error(`❌ DATABASEAPI: BACKEND getCurrentUser STILL RETURNING NON-INTEGER USER ID!`);
                console.error(`❌ DATABASEAPI: Expected integer Django User.id, got:`, data.user.id, typeof data.user.id);
                throw new Error(`Backend getCurrentUser fix failed - still getting UUID instead of Django User.id`);
            }

            if (data?.user) {
                console.log(`✅ DATABASEAPI: getCurrentUser validation passed - Django User.id: ${data.user.id}`);
            }

            return data;
        })
        .catch(error => {
            console.error('DatabaseAPI: Error getting current user:', error);

            if (error.message.includes('Authentication failed')) {
                throw error;
            }

            return null;
        });
}

export function getUserTeams(week = null, year = null) {
    let url = `${API_BASE}/${ENDPOINTS.AUTH.USER_TEAMS}/`;

    const params = new URLSearchParams();
    if (week) {
        params.append('week', week);
    }
    if (year) {
        params.append('year', year);
    }

    if (params.toString()) {
        url += `?${params.toString()}`;
    }

    return fetch(url, {
        credentials: 'include'
    })
        .then(response => {
            console.log(`DatabaseAPI: Get user teams response status: ${response.status}`);

            if (!response.ok) {
                const error = { status: response.status };
                if (handleApiAuthError(error)) {
                    throw new Error('Authentication failed - redirected to login');
                }

                throw new Error(`HTTP ${response.status}: Failed to get user teams`);
            }

            return response.json();
        })
        .then(data => {
            console.log('DatabaseAPI: User teams data:', data);
            return data;
        })
        .catch(error => {
            console.error('DatabaseAPI: Error getting user teams:', error);

            if (error.message.includes('Authentication failed')) {
                throw error;
            }

            return { teams: [] };
        });
}

export function getUserTeamNamingDefaults() {
    console.log(`DatabaseAPI: Getting user team naming defaults`);

    return fetch(`${API_BASE}/${ENDPOINTS.AUTH.TEAM_NAMING_DEFAULTS}/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get user team naming defaults response status: ${response.status}`);

        if (!response.ok) {
            const error = { status: response.status };
            if (handleApiAuthError(error)) {
                throw new Error('Authentication failed - redirected to login');
            }

            throw new Error(`HTTP ${response.status}: Failed to get user team naming defaults`);
        }

        return response.json();
    })
    .then(data => {
        console.log('DatabaseAPI: User team naming defaults:', data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting user team naming defaults:', error);

        if (error.message.includes('Authentication failed')) {
            throw error;
        }

        return {
            default_team_name: '',
            default_known_as: '',
            hide_known_as_default: false
        };
    });
}

export function updateUserTeamNamingDefaults(defaults) {
    console.log(`DatabaseAPI: Updating user team naming defaults:`, defaults);

    return fetch(`${API_BASE}/${ENDPOINTS.AUTH.TEAM_NAMING_DEFAULTS}/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(defaults)
    })
    .then(response => {
        console.log(`DatabaseAPI: Update user team naming defaults response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Update user team naming defaults failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to update user team naming defaults`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Update user team naming defaults successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error updating user team naming defaults:', error);
        throw error;
    });
}

export function getTeamNaming(teamId) {
    console.log(`DatabaseAPI: Getting team naming for team ${teamId}`);

    return fetch(`${API_BASE}/${ENDPOINTS.TEAMS.NAMING}/${teamId}/naming/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get team naming response status: ${response.status}`);

        if (!response.ok) {
            const error = { status: response.status };
            if (handleApiAuthError(error)) {
                throw new Error('Authentication failed - redirected to login');
            }

            throw new Error(`HTTP ${response.status}: Failed to get team naming`);
        }

        return response.json();
    })
    .then(data => {
        console.log('DatabaseAPI: Team naming data:', data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting team naming:', error);

        if (error.message.includes('Authentication failed')) {
            throw error;
        }

        return {
            name: '',
            known_as: '',
            hide_known_as: false,
            can_edit_name: false,
            display_name: ''
        };
    });
}

export function updateTeamNaming(teamId, namingData) {
    console.log(`DatabaseAPI: Updating team naming for team ${teamId}:`, namingData);

    return fetch(`${API_BASE}/${ENDPOINTS.TEAMS.NAMING}/${teamId}/naming/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(namingData)
    })
    .then(response => {
        console.log(`DatabaseAPI: Update team naming response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Update team naming failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to update team naming`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Update team naming successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error updating team naming:', error);
        throw error;
    });
}

export function getUserCurrencyBalance() {
    return fetch(`${API_BASE}/v2/currency/balance/`, {
        credentials: 'include'
    })
    .then(response => {
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to get user balance`);
        }
        return response.json();
    })
    .then(data => {
        console.log('DatabaseAPI: User balance retrieved:', data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting user balance:', error);
        throw error;
    });
}

export function getLeagueCurrencySummary(leagueId, leagueTemplate) {
    const params = new URLSearchParams();
    if (leagueId) params.append('league_id', leagueId);
    if (leagueTemplate) params.append('league_template', leagueTemplate);

    return fetch(`${API_BASE}/v2/currency/league-summary/?${params}`, {
        credentials: 'include'
    })
    .then(response => {
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to get league currency summary`);
        }
        return response.json();
    })
    .then(data => {
        console.log('DatabaseAPI: League currency summary retrieved:', data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting league currency summary:', error);
        throw error;
    });
}

export function processLeagueEntry(leagueData) {
    return fetch(`${API_BASE}/v2/currency/league-entry/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(leagueData)
    })
    .then(response => {
        if (!response.ok) {
            return response.json().then(errorData => {
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to process league entry`);
            });
        }
        return response.json();
    })
    .then(data => {
        console.log('DatabaseAPI: League entry processed:', data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error processing league entry:', error);
        throw error;
    });
}

export function getUserCurrencyRewards(teamId, seasonYear, week) {
    const params = new URLSearchParams({
        team_id: teamId,
        season_year: seasonYear,
        week: week
    });

    return fetch(`${API_BASE}/v2/currency/rewards/?${params}`, {
        credentials: 'include'
    })
    .then(response => {
        if (!response.ok) {
            return response.json().catch(() => null).then(errorData => {
                const envelope = errorData?.error;
                const error = new Error(
                    envelope?.detail || `HTTP ${response.status}: Failed to get user currency rewards`
                );
                error.code = envelope?.code || 'REWARDS_REQUEST_FAILED';
                error.status = response.status;
                error.envelope = errorData;
                throw error;
            });
        }
        return response.json();
    })
    .then(data => {
        console.log('DatabaseAPI: User currency rewards retrieved:', data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting user currency rewards:', error);
        throw error;
    });
}

export function submitBetaKeyRequest(email) {
    console.log(`DatabaseAPI: Submitting beta key request for: ${email}`);

    return fetch(`${API_BASE}/v2/beta-key-requests/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({ email })
    })
    .then(response => {
        console.log(`DatabaseAPI: Beta key request response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Beta key request failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to submit beta key request`);
            });
        }

        return response.json();
    })
    .then(data => {
        // On auto-approval this payload contains a live `beta_key` — never log it.
        console.log(`DatabaseAPI: Beta key request complete, success=${data.success}`);

        // VALIDATE EXPECTED RESPONSE FORMAT - NO SILENT FAILURES
        if (!data.success && data.success !== false) {
            throw new Error('❌ INVALID RESPONSE: Missing "success" field in beta key request response');
        }

        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error submitting beta key request:', error);
        throw error;
    });
}

export function checkBetaKeyRequestStatus(email) {
    console.log(`DatabaseAPI: Checking beta key request status for: ${email}`);

    return fetch(`${API_BASE}/v2/beta-key-requests/check/${encodeURIComponent(email)}/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Check beta key status response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Check beta key status failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to check beta key status`);
            });
        }

        return response.json();
    })
    .then(data => {
        // May echo an existing key back for the address — log status only.
        console.log(`DatabaseAPI: Check beta key status complete, status=${data.status}`);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error checking beta key status:', error);
        throw error;
    });
}

// ============================================================================
// FRIENDSHIP API
// ============================================================================

/**
 * Get every active player (user who owns at least one team) for friend pickers.
 *
 * There is no dedicated "list all users" endpoint, and adding one is backend
 * work. This reuses the existing, already-authenticated global leaderboard
 * contract (GET /api/v2/leaderboard/ — GlobalLeaderboardView), which returns
 * exactly the active-player set we need:
 *
 *   User.objects.filter(owned_teams__isnull=False) → { public_id, username, teams }
 *
 * Only the public identity fields are projected here; leaderboard scores are
 * discarded so no consumer accidentally treats this as ranking data.
 *
 * @returns {Promise<{players: Array<{public_id: string, username: string, teams: string[]}>}>}
 */
export function getActivePlayerDirectory() {
    console.log('DatabaseAPI: Getting active player directory (via global leaderboard)');

    return fetch(`${API_BASE}/v2/leaderboard/?filter_type=highscore&timeframe=lifetime`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        if (!response.ok) {
            const error = { status: response.status };
            if (handleApiAuthError(error)) {
                throw new Error('Authentication failed - redirected to login');
            }

            return response.text().then(body => {
                throw new Error(
                    `HTTP ${response.status}: Failed to load active player directory — ${body || 'no response body'}`
                );
            });
        }

        return response.json();
    })
    .then(data => {
        // VALIDATE RESPONSE - NO SILENT FAILURES
        if (!Array.isArray(data.leaderboard)) {
            throw new Error(
                `❌ INVALID RESPONSE: Expected "leaderboard" array from /v2/leaderboard/, ` +
                `received keys [${Object.keys(data || {}).join(', ')}]`
            );
        }

        const players = data.leaderboard.map((entry, index) => {
            if (!entry.public_id) {
                throw new Error(
                    `❌ INVALID RESPONSE: Leaderboard entry at index ${index} is missing public_id`
                );
            }
            if (!entry.username) {
                throw new Error(
                    `❌ INVALID RESPONSE: Leaderboard entry at index ${index} ` +
                    `(public_id=${entry.public_id}) is missing username`
                );
            }

            return {
                public_id: entry.public_id,
                username: entry.username,
                teams: Array.isArray(entry.teams) ? entry.teams : []
            };
        });

        players.sort((a, b) => a.username.localeCompare(b.username, undefined, { sensitivity: 'base' }));

        console.log(`DatabaseAPI: Active player directory returned ${players.length} player(s)`);

        return { players };
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting active player directory:', error);
        throw error;
    });
}

/**
 * Get list of user's friends with recent league data
 * @param {Object} options - Query options
 * @param {number} options.limit - Max number of recent leagues per friend (default: 20)
 * @param {string} options.sport - Filter by sport: 'football' or 'baseball'
 * @returns {Promise<Array>} Array of friend objects with recent leagues
 */
export function getFriends({ limit = 20, sport = null } = {}) {
    console.log(`DatabaseAPI: Getting friends list (limit: ${limit}, sport: ${sport || 'all'})`);

    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    if (sport) params.append('sport', sport);

    const queryString = params.toString();
    const url = `${API_BASE}/platform/friends/${queryString ? '?' + queryString : ''}`;

    return fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get friends response status: ${response.status}`);

        if (!response.ok) {
            const error = { status: response.status };
            if (handleApiAuthError(error)) {
                throw new Error('Authentication failed - redirected to login');
            }

            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Get friends failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to get friends`);
            }).catch(() => {
                throw new Error(`HTTP ${response.status}: Failed to get friends`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Friends list retrieved:`, data);
        
        // VALIDATE RESPONSE - NO SILENT FAILURES
        if (!Array.isArray(data.friends)) {
            throw new Error('❌ INVALID RESPONSE: Expected "friends" array in response');
        }

        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting friends:', error);
        throw error;
    });
}

/**
 * Add a friend by public_id or username
 * @param {Object} friendData
 * @param {string} friendData.public_id - Friend's public UUID (optional if username provided)
 * @param {string} friendData.username - Friend's username (optional if public_id provided)
 * @returns {Promise<Object>} Success response with friend data
 */
export function addFriend({ public_id = null, username = null }) {
    console.log(`DatabaseAPI: Adding friend (public_id: ${public_id}, username: ${username})`);

    // VALIDATE INPUT - NO SILENT FAILURES
    if (!public_id && !username) {
        throw new Error('❌ addFriend: Must provide either public_id or username');
    }

    const body = {};
    if (public_id) body.public_id = public_id;
    if (username) body.username = username;

    return fetch(`${API_BASE}/platform/friends/add/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(body)
    })
    .then(response => {
        console.log(`DatabaseAPI: Add friend response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Add friend failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to add friend`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Friend added successfully:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error adding friend:', error);
        throw error;
    });
}

/**
 * Remove a friend
 * @param {string} publicId - Friend's public UUID to remove
 * @returns {Promise<Object>} Success response
 */
export function removeFriend(publicId) {
    console.log(`DatabaseAPI: Removing friend (public_id: ${publicId})`);

    // VALIDATE INPUT - NO SILENT FAILURES
    if (!publicId || typeof publicId !== 'string' || !UUID_PATTERN.test(publicId)) {
        throw new Error(`❌ removeFriend: Invalid public_id "${publicId}" - must be a UUID string`);
    }

    return fetch(`${API_BASE}/platform/friends/${publicId}/`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Remove friend response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Remove friend failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to remove friend`);
            }).catch(() => {
                throw new Error(`HTTP ${response.status}: Failed to remove friend`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Friend removed successfully:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error removing friend:', error);
        throw error;
    });
}

/**
 * Search for users to add as friends.
 *
 * Backend contract: GET /api/platform/friends/search/?q=<query>&limit=<n>
 * (SearchUsersView) matches `username__icontains`, excludes the caller, and
 * defaults to `limit=10` when the parameter is omitted.
 *
 * @param {string} query - Search query (minimum 3 characters after trimming)
 * @param {Object} [options]
 * @param {number} [options.limit=50] - Max results. The backend default of 10
 *        truncates common substrings, so callers browsing for a player get a
 *        misleading "no match" for a user who does exist further down the list.
 * @returns {Promise<{users: Array, count: number}>} User search results
 */
export function searchUsers(query, { limit = 50 } = {}) {
    // VALIDATE INPUT - NO SILENT FAILURES
    if (typeof query !== 'string') {
        throw new Error(`❌ searchUsers: Query must be a string, received ${typeof query}`);
    }

    if (!Number.isInteger(limit) || limit < 1) {
        throw new Error(`❌ searchUsers: limit must be a positive integer, received ${limit}`);
    }

    // Usernames pasted out of the Django admin routinely carry leading/trailing
    // whitespace. `username__icontains` treats that whitespace literally, so an
    // untrimmed query matches nothing even though the account exists. Trim before
    // validating the length and before sending.
    const trimmedQuery = query.trim();

    if (trimmedQuery.length < 3) {
        throw new Error(
            `❌ searchUsers: Query must be at least 3 characters after trimming ` +
            `(received ${query.length} raw / ${trimmedQuery.length} trimmed characters)`
        );
    }

    console.log(`DatabaseAPI: Searching users (${trimmedQuery.length} chars, limit ${limit})`);

    const params = new URLSearchParams({ q: trimmedQuery, limit: String(limit) });

    return fetch(`${API_BASE}/platform/friends/search/?${params}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Search users response status: ${response.status}`);

        if (!response.ok) {
            const error = { status: response.status };
            if (handleApiAuthError(error)) {
                throw new Error('Authentication failed - redirected to login');
            }

            // Read the body as text first. The previous implementation chained a
            // `.catch()` onto the `throw` inside `.then()`, which swallowed every
            // backend error message and always surfaced a generic HTTP failure —
            // the real reason a search failed was impossible to see from the UI.
            return response.text().then(body => {
                let detail = body;
                try {
                    const parsed = JSON.parse(body);
                    detail = parsed.error || parsed.detail || body;
                } catch (parseError) {
                    // Body was not JSON (HTML error page, proxy failure); keep raw text.
                }
                console.error(`DatabaseAPI: Search users failed (HTTP ${response.status}): ${detail}`);
                throw new Error(`HTTP ${response.status}: ${detail || 'Failed to search users'}`);
            });
        }

        return response.json();
    })
    .then(data => {
        // VALIDATE RESPONSE - NO SILENT FAILURES
        if (!Array.isArray(data.results)) {
            throw new Error(
                `❌ INVALID RESPONSE: Expected "results" array from /platform/friends/search/, ` +
                `received keys [${Object.keys(data || {}).join(', ')}]`
            );
        }

        console.log(`DatabaseAPI: User search returned ${data.results.length} result(s)`);

        // Return in expected format (results → users for consistency)
        return {
            users: data.results,
            count: data.count
        };
    })
    .catch(error => {
        console.error('DatabaseAPI: Error searching users:', error);
        throw error;
    });
}

/**
 * Get list of pending friend requests (incoming requests)
 * @returns {Promise<{pending_requests: Array<{public_id: string, username: string, request_sent_at: string}>, count: number}>}
 */
export function getPendingFriendRequests() {
    console.log(`DatabaseAPI: Getting pending friend requests`);

    return fetch(`${API_BASE}/platform/friends/pending/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get pending requests response status: ${response.status}`);

        if (!response.ok) {
            const error = { status: response.status };
            if (handleApiAuthError(error)) {
                throw new Error('Authentication failed - redirected to login');
            }

            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Get pending requests failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to get pending requests`);
            }).catch(() => {
                throw new Error(`HTTP ${response.status}: Failed to get pending requests`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Pending friend requests retrieved:`, data);
        
        // VALIDATE RESPONSE - NO SILENT FAILURES
        if (!Array.isArray(data.pending_requests)) {
            throw new Error('❌ INVALID RESPONSE: Expected "pending_requests" array in response');
        }

        // Transform backend format (from_public_id, from_username) to frontend format (public_id, username)
        const transformedRequests = data.pending_requests.map(request => ({
            public_id: request.from_public_id,
            username: request.from_username,
            request_sent_at: request.request_sent_at
        }));

        return {
            pending_requests: transformedRequests,
            count: data.count || transformedRequests.length
        };
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting pending requests:', error);
        throw error;
    });
}

/**
 * Accept a friend request from a user
 * @param {string} publicId - Public UUID of the person who sent the request
 * @returns {Promise<Object>} Success response with friendship data
 */
export function acceptFriendRequest(publicId) {
    console.log(`DatabaseAPI: Accepting friend request from user ${publicId}`);

    // VALIDATE INPUT - NO SILENT FAILURES
    if (!publicId || typeof publicId !== 'string' || !UUID_PATTERN.test(publicId)) {
        throw new Error(`❌ acceptFriendRequest: Invalid public_id "${publicId}" - must be a UUID string`);
    }

    return fetch(`${API_BASE}/platform/friends/accept/${publicId}/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Accept friend request response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Accept friend request failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to accept friend request`);
            }).catch(() => {
                throw new Error(`HTTP ${response.status}: Failed to accept friend request`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Friend request accepted successfully:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error accepting friend request:', error);
        throw error;
    });
}

/**
 * Decline a friend request from a user
 * @param {string} publicId - Public UUID of the person who sent the request
 * @returns {Promise<Object>} Success response
 */
export function declineFriendRequest(publicId) {
    console.log(`DatabaseAPI: Declining friend request from user ${publicId}`);

    // VALIDATE INPUT - NO SILENT FAILURES
    if (!publicId || typeof publicId !== 'string' || !UUID_PATTERN.test(publicId)) {
        throw new Error(`❌ declineFriendRequest: Invalid public_id "${publicId}" - must be a UUID string`);
    }

    return fetch(`${API_BASE}/platform/friends/decline/${publicId}/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Decline friend request response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Decline friend request failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to decline friend request`);
            }).catch(() => {
                throw new Error(`HTTP ${response.status}: Failed to decline friend request`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Friend request declined successfully:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error declining friend request:', error);
        throw error;
    });
}

/**
 * Get user's completed league history
 * @returns {Promise<Object>} Response with leagues array and count
 */
export function getUserLeagues() {
    console.log(`DatabaseAPI: Getting user league history`);

    return fetch(`${API_BASE}/platform/user-leagues/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get user leagues response status: ${response.status}`);

        if (!response.ok) {
            const error = { status: response.status };
            if (handleApiAuthError(error)) {
                throw new Error('Authentication failed - redirected to login');
            }

            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Get user leagues failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to get user leagues`);
            }).catch(() => {
                throw new Error(`HTTP ${response.status}: Failed to get user leagues`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: User leagues retrieved:`, data);
        
        // VALIDATE RESPONSE - NO SILENT FAILURES
        if (!Array.isArray(data.leagues)) {
            throw new Error('❌ INVALID RESPONSE: Expected "leagues" array in response');
        }

        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting user leagues:', error);
        throw error;
    });
}

/**
 * Get current user's aggregate statistics across all teams
 * @returns {Promise<{wins: number, top_3_finishes: number, top_score_ever: number, top_player_score_ever: number}>}
 */
export function getUserStats() {
    console.log(`DatabaseAPI: Getting user stats`);

    return fetch(`${API_BASE}/platform/user-stats/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get user stats response status: ${response.status}`);

        if (!response.ok) {
            const error = { status: response.status };
            if (handleApiAuthError(error)) {
                throw new Error('Authentication failed - redirected to login');
            }

            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Get user stats failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to get user stats`);
            }).catch(() => {
                throw new Error(`HTTP ${response.status}: Failed to get user stats`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: User stats retrieved:`, data);
        
        // VALIDATE RESPONSE - NO SILENT FAILURES
        if (data.wins === undefined || data.top_3_finishes === undefined ||
            data.top_score_ever === undefined || data.top_player_score_ever === undefined) {
            throw new Error(`❌ INVALID RESPONSE: Missing required fields in user stats - got ${JSON.stringify(data)}`);
        }

        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting user stats:', error);
        throw error;
    });
}

/**
 * Get current user's drafted-card library (grouped by stable card identity:
 * gsis_id for PLAYER cards, gear_catalog_pk for GEAR cards)
 * @returns {Promise<{cards: Array<{card_name: string, card_type: string, position: string, nfl_team: string, rarity_tier: string, gear_slot: string, gsis_id: string|null, gear_catalog_pk: number|null, times_drafted: number, last_drafted_at: string}>, total_records: number}>}
 */
export function getUserLibrary() {
    console.log(`DatabaseAPI: Getting user card library`);

    return fetch(`${API_BASE}/platform/user-library/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get user library response status: ${response.status}`);

        if (!response.ok) {
            const error = { status: response.status };
            if (handleApiAuthError(error)) {
                throw new Error('Authentication failed - redirected to login');
            }

            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Get user library failed:`, errorData);
                throw new Error(errorData.error || `HTTP ${response.status}: Failed to get user library`);
            }).catch(() => {
                throw new Error(`HTTP ${response.status}: Failed to get user library`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: User library retrieved: ${data.total_records} records`);

        // VALIDATE RESPONSE - NO SILENT FAILURES
        if (!Array.isArray(data.cards)) {
            throw new Error(`❌ INVALID RESPONSE: cards is not an array - got ${JSON.stringify(data)}`);
        }
        if (data.total_records === undefined) {
            throw new Error(`❌ INVALID RESPONSE: Missing total_records in user library - got ${JSON.stringify(data)}`);
        }

        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error getting user library:', error);
        throw error;
    });
}
